import { useState, useCallback, useMemo, useEffect } from 'react'

import { FALLBACK_COLOR_MODE } from '../../../shared/constants/colorMode.js'

// ── Types ──────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} ColorModeCapability
 * @property {string} id - Unique color mode identifier.
 */

/**
 * @typedef {Object} UseColorModeParams
 * @property {ColorModeCapability[]} [activeColorModes=[]] - Array of active color mode capability objects.
 * @property {string} [defaultColorMode='rooms'] - Initial color mode ID to set on mount.
 */

/**
 * @typedef {Object} UseColorModeResult
 * @property {string} colorMode - Currently active color mode ID.
 * @property {string[]} availableColorModes - List of available color mode string IDs for UI consumers.
 * @property {(mode: string) => void} setColorMode - Setter function to switch active mode with validation guard.
 */

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Manages active color mode selection (rooms / status / insolation / compare / etc.)
 * shared across domain view components.
 *
 * @param {UseColorModeParams} params
 * @returns {UseColorModeResult}
 */
export function useColorMode({ 
  activeColorModes = [],
  defaultColorMode = FALLBACK_COLOR_MODE
 }) {

  const availableColorModes = useMemo(() => {
    return activeColorModes.map((mode) => mode.id)
  }, [activeColorModes])

  const [colorMode, setColorModeRaw] = useState(defaultColorMode)

  useEffect(() => {
    setColorModeRaw((currentMode) => {
      // Preserve an explicit user selection as long as that mode remains
      // available. Context/filter updates often recreate activeColorModes by
      // reference and must not reset the selection to the configured default.
      if (availableColorModes.includes(currentMode)) return currentMode
      if (availableColorModes.includes(defaultColorMode)) return defaultColorMode
      return availableColorModes[0] ?? FALLBACK_COLOR_MODE
    })
  }, [defaultColorMode, availableColorModes])

  // Guard setter — ignore values not in availableColorModes.
  const setColorMode = useCallback(
    (mode) => {
      if (availableColorModes.includes(mode)) {
        setColorModeRaw(mode)
      }
    },
    [availableColorModes]
  )

  return { colorMode, setColorMode, availableColorModes }
}
